import {
  Box,
  Checkbox,
  FormControl,
  FormControlLabel,
  FormGroup,
  FormHelperText,
  InputLabel,
  MenuItem,
  Select,
  Typography,
} from '@mui/material';
import { useMemo } from 'react';

import { MemberFormData } from '../types/member.types';

import styles from './department.module.css';

import { Department } from '@/features/companies';
import { EXCLUSION_DEPARTMENTS, OTHER_DEPARTMENT_CODE } from '@/lib/constants';
import { getStylesForEditableField } from '@/utils/helpers';
import { PaperBoxStyles } from '@/components/styles';

enum DepartmentActionType {
  SELECT_ALL = 'select_all',
  CLEAR_ALL = 'clear_all',
  ADD = 'add',
  REMOVE = 'remove',
  TOGGLE_OTHER = 'toggle_other',
}

interface DepartmentSectionProps {
  formData: MemberFormData;
  memberData: MemberFormData | null;
  departments: Department[];
  departmentsLoading: boolean;
  onUpdateField: (field: keyof MemberFormData, value: unknown) => void;
  isFieldValid: (field: keyof MemberFormData) => boolean;
  getFieldError: (field: keyof MemberFormData) => string | undefined;
  isEditMode?: boolean;
}

export const DepartmentSection = ({
  formData,
  memberData,
  departments,
  departmentsLoading,
  onUpdateField,
  isFieldValid,
  getFieldError,
  isEditMode = false,
}: DepartmentSectionProps) => {
  const currentDepartments = departments.filter(
    (dept) => !EXCLUSION_DEPARTMENTS.includes(dept.name)
  );

  const relatedDepartments = departments.filter((dept) => dept.code !== OTHER_DEPARTMENT_CODE);
  const activeDepartmentCodes = departments.filter(
    (dept) => !EXCLUSION_DEPARTMENTS.includes(dept.name) && dept.code !== OTHER_DEPARTMENT_CODE
  );

  const isCheckAll = useMemo(() => {
    const actualActiveDepartmentCodes = formData.relatedStaffDepartmentCodes.filter((code) =>
      activeDepartmentCodes.some((dept) => (dept.code || 0) === code)
    );
    return (
      actualActiveDepartmentCodes.length > 0 &&
      actualActiveDepartmentCodes.length >= activeDepartmentCodes.length
    );
  }, [formData.relatedStaffDepartmentCodes, activeDepartmentCodes]);

  const isCheckAllOriginal = useMemo(() => {
    const actualActiveDepartmentCodes = memberData?.relatedStaffDepartmentCodes?.filter((code) =>
      activeDepartmentCodes.some((dept) => (dept.code || 0) === code)
    );
    return (
      !!actualActiveDepartmentCodes &&
      actualActiveDepartmentCodes.length > 0 &&
      actualActiveDepartmentCodes.length >= activeDepartmentCodes.length
    );
  }, [memberData?.relatedStaffDepartmentCodes, activeDepartmentCodes]);

  const handleDepartmentChange = (value: string) => {
    const departmentCodeNum = value;
    onUpdateField('bucode', departmentCodeNum);
  };

  const handleDepartmentAction = (action: DepartmentActionType, departmentCode?: string) => {
    switch (action) {
      case DepartmentActionType.SELECT_ALL: {
        const allRoleIds = activeDepartmentCodes.map((role) => role.code || 0);
        onUpdateField('relatedStaffDepartmentCodes', allRoleIds);
        break;
      }
      case DepartmentActionType.CLEAR_ALL:
        onUpdateField('relatedStaffDepartmentCodes', []);
        break;
      case DepartmentActionType.ADD:
        if (departmentCode) {
          const codeNum = Number(departmentCode);
          if (!formData.relatedStaffDepartmentCodes.includes(codeNum)) {
            onUpdateField('relatedStaffDepartmentCodes', [
              ...formData.relatedStaffDepartmentCodes,
              codeNum,
            ]);
          }
        }
        break;
      case DepartmentActionType.REMOVE:
        if (departmentCode) {
          const codeNum = Number(departmentCode);
          onUpdateField(
            'relatedStaffDepartmentCodes',
            formData.relatedStaffDepartmentCodes.filter((code) => code !== codeNum)
          );
        }
        break;
      case DepartmentActionType.TOGGLE_OTHER:
        onUpdateField('isOtherRelatedDepartment', !formData.isOtherRelatedDepartment);
        break;
    }
  };

  return (
    <PaperBoxStyles elevation={1} sx={{ mb: 3, paddingBottom: '32px' }}>
      <Typography variant='h5' fontWeight='bold' className={styles.sectionTitle}>
        部署選択
      </Typography>
      <Typography className={styles.subTitle}>所属部署</Typography>
      <Box className={styles.sectionGrid}>
        <Box className={styles.row}>
          <FormControl
            fullWidth
            required
            error={!isFieldValid('bucode')}
            disabled={departmentsLoading}
          >
            <InputLabel>所属部署</InputLabel>
            <Select
              value={formData.bucode?.toString() || ''}
              onChange={(e) => handleDepartmentChange(e.target.value)}
              label='所属部署'
              sx={getStylesForEditableField(
                isEditMode && !!memberData,
                memberData?.bucode,
                formData.bucode
              )}
            >
              {currentDepartments?.map((dept) => (
                <MenuItem key={dept.id} value={dept.code?.toString() || ''}>
                  {dept.name}
                </MenuItem>
              ))}
            </Select>
            <FormHelperText>{getFieldError('bucode')}</FormHelperText>
          </FormControl>
        </Box>
      </Box>
      <Typography className={styles.subTitle}>関連部署</Typography>
      <Box
        className={styles.authoritySection}
        sx={getStylesForEditableField(
          isEditMode && !!memberData,
          [
            isCheckAllOriginal.toString(),
            memberData?.isOtherRelatedDepartment?.toString() || 'false',
          ],
          [isCheckAll.toString(), formData.isOtherRelatedDepartment?.toString() || 'false']
        )}
      >
        <FormGroup className={styles.authorityGroup} aria-required>
          <FormControlLabel
            onChange={(_, checked: boolean) =>
              handleDepartmentAction(
                checked ? DepartmentActionType.SELECT_ALL : DepartmentActionType.CLEAR_ALL
              )
            }
            control={<Checkbox checked={isCheckAll} />}
            label={'すべて'}
          />
          <FormControlLabel
            onChange={() => handleDepartmentAction(DepartmentActionType.TOGGLE_OTHER)}
            control={<Checkbox checked={formData.isOtherRelatedDepartment || false} />}
            label={'その他'}
          />
        </FormGroup>
        <FormHelperText className={styles.errorHelperText}></FormHelperText>
      </Box>
      <Box
        className={styles.authoritySection}
        sx={getStylesForEditableField(
          isEditMode && !!memberData,
          memberData?.relatedStaffDepartmentCodes,
          formData.relatedStaffDepartmentCodes
        )}
      >
        <FormGroup className={styles.departmentGroup} aria-required>
          {relatedDepartments.map((department) => (
            <FormControlLabel
              key={department.id}
              onChange={(_, checked) =>
                handleDepartmentAction(
                  checked ? DepartmentActionType.ADD : DepartmentActionType.REMOVE,
                  department.code?.toString()
                )
              }
              disabled={EXCLUSION_DEPARTMENTS.includes(department.name)}
              control={
                <Checkbox
                  checked={formData.relatedStaffDepartmentCodes.includes(department.code || 0)}
                />
              }
              label={department.name}
              value={department.code?.toString() || ''}
            />
          ))}
        </FormGroup>
        <FormHelperText className={styles.errorHelperText}></FormHelperText>
      </Box>
    </PaperBoxStyles>
  );
};
